import { LockOutlined } from '@ant-design/icons';
import { Button, Card, Divider, Form, Input, Tabs, Tag, Typography } from 'antd';
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import { getUserRoleLabel } from '../config/rbac/rbac.config';
import { changePassword } from '../services/userService';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const ProfilePage = () => {
    const { currentUser } = useAuth();
    const { country } = useConfig();
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const handleChangePassword = async (values) => {
        try {
            setLoading(true);

            await changePassword(
                currentUser.id,
                values.currentPassword,
                values.newPassword
            );

            window.messageApi.success('Contraseña actualizada correctamente');
            form.resetFields();
        } catch (error) {
            window.messageApi.error(error.message || 'Error al cambiar la contraseña');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '24px' }}>
            <Tabs defaultActiveKey="info">
                <TabPane tab="Información Personal" key="info">
                    <Card title="Mi Perfil" style={{ maxWidth: 600, margin: '0 auto' }}>
                        <Title level={4}>Información Personal</Title>

                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Nombre: </Text>
                            <Text>{currentUser?.firstName || 'No disponible'}</Text>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Apellidos: </Text>
                            <Text>{currentUser?.lastName || 'No disponible'}</Text>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Usuario: </Text>
                            <Text>{currentUser?.username || 'No disponible'}</Text>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Correo: </Text>
                            <Text>{currentUser?.email || 'No disponible'}</Text>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Tipo de Usuario: </Text>
                            <Tag color={currentUser?.Admin ? 'green' : 'blue'}>
                                {getUserRoleLabel(country?.code, currentUser?.Admin)}
                            </Tag>
                        </div>

                        {currentUser?.No && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong>Número BC: </Text>
                                <Text>{currentUser.No}</Text>
                            </div>
                        )}

                        {currentUser?.Customer_Price_Group && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong>Grupo de Precios: </Text>
                                <Text>{currentUser.Customer_Price_Group}</Text>
                            </div>
                        )}

                        <Divider />

                        <Title level={5}>Información del Sistema</Title>

                        {currentUser?.createdAt && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong>Fecha de Creación: </Text>
                                <Text>{new Date(currentUser.createdAt).toLocaleDateString('es-ES')}</Text>
                            </div>
                        )}

                        {currentUser?.lastModified && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong>Última Modificación: </Text>
                                <Text>{new Date(currentUser.lastModified).toLocaleDateString('es-ES')}</Text>
                            </div>
                        )}

                        {currentUser?.lastSynced && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong>Última Sincronización: </Text>
                                <Text>{new Date(currentUser.lastSynced).toLocaleDateString('es-ES')}</Text>
                            </div>
                        )}

                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Estado de Sincronización: </Text>
                            <Tag color={currentUser?.pendingSync ? 'orange' : 'green'}>
                                {currentUser?.pendingSync ? 'Pendiente' : 'Sincronizado'}
                            </Tag>
                        </div>
                    </Card>
                </TabPane>

                <TabPane tab="Cambiar Contraseña" key="password">
                    <Card title="Cambiar Contraseña" style={{ maxWidth: 600, margin: '0 auto' }}>
                        <Form
                            form={form}
                            name="changePassword"
                            onFinish={handleChangePassword}
                            layout="vertical"
                        >
                            <Form.Item
                                name="currentPassword"
                                label="Contraseña Actual"
                                rules={[
                                    { required: true, message: 'Por favor ingrese su contraseña actual' }
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="Ingrese su contraseña actual"
                                />
                            </Form.Item>

                            <Divider />

                            <Form.Item
                                name="newPassword"
                                label="Nueva Contraseña"
                                rules={[
                                    { required: true, message: 'Por favor ingrese su nueva contraseña' },
                                    { min: 6, message: 'La contraseña debe tener al menos 6 caracteres' }
                                ]}
                                hasFeedback
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="Ingrese su nueva contraseña"
                                />
                            </Form.Item>

                            <Form.Item
                                name="confirmPassword"
                                label="Confirmar Nueva Contraseña"
                                dependencies={['newPassword']}
                                hasFeedback
                                rules={[
                                    { required: true, message: 'Por favor confirme su nueva contraseña' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('newPassword') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Las contraseñas no coinciden'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="Confirme su nueva contraseña"
                                />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    block
                                >
                                    Cambiar Contraseña
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </TabPane>
            </Tabs>
        </div>
    );
};

export default ProfilePage; 